import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {isArray, isString} from 'lodash-es';
import {ImportExportMode} from '../../pages/subjects/export-subject-file-dialog/export-subject-file-dialog.component';
import {FilterComponent, FilterEvent, FilterEventForm, FilterField, SearchPlaceholderContext} from './filter/filter.component';
import {INITIAL_PAGE_NUMBER, INITIAL_PAGE_SIZE, PageEvent} from './paging/paginator.component';
import {Row, SortEvent, TableComponent} from './table/table.component';

export const initialPageEvent = <PageEvent>{
    pageSize: INITIAL_PAGE_SIZE,
    pageNumber: INITIAL_PAGE_NUMBER,
    pageOffset: INITIAL_PAGE_NUMBER * INITIAL_PAGE_SIZE,
};

export const initialTableEvent: TableEvent = {
    page: initialPageEvent,
    spinnerId: 'initial',
};

@Component({
    selector: 'af-full-table',
    templateUrl: 'full-table.component.html',
    standalone: false,
})
export class FullTableComponent implements OnInit {
    @ViewChild('filter') filter: FilterComponent;
    @ViewChild('table') table: TableComponent;
    @Input() filterFields: FilterField[];
    @Input() hasFilterContent: boolean = false;
    @Input() filterEvent: FilterEvent;
    @Input() pageEvent: PageEvent;
    @Input() sortEvent: SortEvent;
    @Input() dataSource: Row[];
    @Input() chips: string[];
    @Input() chipsFormControlName: keyof FilterEventForm;
    @Input() exportImportMode: ImportExportMode;
    @Input() showSearchBar: boolean = true;
    @Input() showImport: boolean;
    @Input() showExport: boolean;
    @Input() allowSearchSpaces: boolean = true;
    @Input() addTableTranslationPrefix: boolean = true;
    @Input() columnTranslationPrefixes: Record<string, string> = {};
    @Input() placeholderContext: SearchPlaceholderContext;
    @Input() totalElements: number;
    @Input() totalElementsTranslationKey: string;
    @Input() emptyStateTitleTranslationKey: string;
    @Input() emptyStateSubtitleTranslationKey: string;
    @Input() emptyStateIconName: string;
    @Input() emptyStateButtonLabel: string;
    @Input() emptyStateSearchSubtitleTranslationKey: string;
    @Input() emptyStateSearchTitleTranslationKey: string;
    @Input() emptyStateSearchIconName: string;
    @Input() emptyStateSearchButtonLabel: string;
    @Input() showFilter: boolean = true;
    @Input() showTable: boolean = true;
    @Input() showPaginator: boolean = true;
    @Input() additionalSpinnerIds: string[] | string;
    @Output() emptyStateClickListener = new EventEmitter<void>();
    @Output() tableUpdateListener = new EventEmitter<TableEvent>();

    spinnerIds: string[] | string = ['initial', 'filter', 'sort', 'paging'];

    tableEvent = <TableEvent>{
        spinnerId: 'initial',
        page: {
            pageNumber: INITIAL_PAGE_NUMBER,
            pageSize: INITIAL_PAGE_SIZE,
        },
    };

    private userFilterApplied = false;

    get emptyStateTitle(): string {
        return this.isFilterApplied() && !this.dataSource.length
            ? (this.emptyStateSearchTitleTranslationKey || 'i18n_af_emptyStateTitleSearchResult')
            : this.emptyStateTitleTranslationKey;
    }

    get emptyStateSubtitle(): string {
        return this.isFilterApplied() && !this.dataSource.length
            ? (this.emptyStateSearchSubtitleTranslationKey || 'i18n_af_emptyStateSubtitleSearchResult')
            : this.emptyStateSubtitleTranslationKey;
    }

    get emptyStateIcon(): string {
        return this.isFilterApplied() && !this.dataSource.length
            ? (this.emptyStateSearchIconName || 'ic_empty_state_not_found')
            : this.emptyStateIconName;
    }

    get emptyStateButton(): string {
        if (this.isFilterApplied() && !this.dataSource.length) {
            return this.emptyStateSearchButtonLabel ?? 'i18n_af_buttonNewSearch';
        }
        return this.emptyStateButtonLabel;
    }

    ngOnInit(): void {
        if (!this.additionalSpinnerIds) {
            return;
        }

        if (isString(this.additionalSpinnerIds)) {
            this.spinnerIds = [...this.spinnerIds, this.additionalSpinnerIds];
        } else if (isArray(this.additionalSpinnerIds)) {
            this.spinnerIds = [...this.spinnerIds, ...this.additionalSpinnerIds];
        }
    }

    filterTable(event: FilterEvent): void {
        this.userFilterApplied = true;
        this.tableEvent = {
            ...this.tableEvent,
            filter: event,
            spinnerId: 'filter',
        };
        this.tableUpdateListener.emit(this.tableEvent);
    }

    sortBy(event: SortEvent): void {
        this.tableEvent = {
            ...this.tableEvent,
            filter: this.filterEvent,
            sortBy: event,
            spinnerId: 'sort',
        };
        this.tableUpdateListener.emit(this.tableEvent);
    }

    pageChanges(event: PageEvent): void {
        this.tableEvent = {
            ...this.tableEvent,
            filter: this.filterEvent,
            page: {
                pageSize: event.pageSize,
                pageNumber: event.pageNumber,
                pageOffset: event.pageOffset,
            },
            spinnerId: 'paging',
        };
        this.tableUpdateListener.emit(this.tableEvent);
    }

    onEmptyStateClick(): void {
        if (this.isFilterApplied() && !this.dataSource.length) {
            this.userFilterApplied = false;
            this.filter.reset();
            this.tableEvent = {
                ...this.tableEvent,
                filter: {},
                spinnerId: 'filter',
            };
            this.tableUpdateListener.emit(this.tableEvent);
        } else if (this.emptyStateClickListener.observed) {
            this.emptyStateClickListener.emit();
        }
    }

    private isFilterApplied(): boolean {
        return this.userFilterApplied;
    }
}

export function mergeTableEvent(prev: TableEvent | undefined, incoming: TableEvent): TableEvent {
    const prevEvent = prev ?? ({} as TableEvent);
    const prevFilter = prevEvent.filter ?? {};
    const isFilterEvent = (incoming?.spinnerId === 'filter');
    const incomingFilter = isFilterEvent && Object.prototype.hasOwnProperty.call(incoming, 'filter')
        ? (incoming.filter ?? {})
        : prevFilter;
    const resolvedPage: PageEvent = isFilterEvent
        ? {...(prevEvent.page ?? initialTableEvent.page)}
        : {
            ...(prevEvent.page ?? ({} as PageEvent)),
            ...(incoming.page ?? ({} as PageEvent)),
            pageOffset: incoming.page?.pageOffset ?? prevEvent.page?.pageOffset ?? 0,
        };
    return {
        ...prevEvent,
        ...incoming,
        page: resolvedPage,
        filter: incomingFilter,
    };
}

export interface TableEvent {
    filter?: FilterEvent;
    sortBy?: SortEvent;
    page?: PageEvent;
    spinnerId?: string;
}
